/**
 * OVERRIDE ObjectBrowserBody.jsx
 * REASON:
 * FILE VERSION: 18.10.1 volto main (2025-06-05)
 * PULL REQUEST:
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/693
 * DEVELOPER: iFlameing
 * CHANGELOG: We need to override because multiple mode always start from root. I have changed it to start from current path or provided one.
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */
/**
 * ObjectBrowserWidget component.
 * @module components/manage/Widgets/ObjectBrowserWidget
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Input, Segment, Breadcrumb } from 'semantic-ui-react';
import { Switch } from '@plone/components';
import { v4 as uuid } from 'uuid';

import join from 'lodash/join';

// These absolute imports (without using the corresponding centralized index.js) are required
// to cut circular import problems, this file should never use them. This is because of
// the very nature of the functionality of the component and its relationship with others
import { searchContent } from '@plone/volto/actions/search/search';
import { createContent } from '@plone/volto/actions/content/content';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import {
  flattenToAppURL,
  isInternalURL,
  getBaseUrl,
} from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

import folderSVG from '@plone/volto/icons/folder.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import searchSVG from '@plone/volto/icons/zoom.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import homeSVG from '@plone/volto/icons/home.svg';
import iconsSVG from '@plone/volto/icons/apps.svg';
import listSVG from '@plone/volto/icons/list-bullet.svg';
import expandSVG from '@plone/volto/icons/left-key.svg';
import collapseSVG from '@plone/volto/icons/right-key.svg';
import cloudSVG from '@plone/volto/icons/cloud.svg';
import sortDownSVG from 'volto-fhnw-web25-base/icons/sort-down.svg';
import checkSVG from '@plone/volto/icons/check.svg';

import ObjectBrowserNav from '@plone/volto/components/manage/Sidebar/ObjectBrowserNav';
import { objectBrowserMessages } from '@fhnw/helpers/ObjectBrowser/ObjectBrowserInternationalization.ts';

// How many items to request initially
const INITIAL_SEARCH_LENGTH = 100;
const INCREASE_SEARCH_LENGTH = 100;

// For manual testing
// const INITIAL_SEARCH_LENGTH = 4;
// const INCREASE_SEARCH_LENGTH = 4;

const messages = objectBrowserMessages;

function getParentURL(url) {
  return flattenToAppURL(`${join(url.split('/').slice(0, -1), '/')}`) || '/';
}

/**
 * ObjectBrowserBody container class.
 * @class ObjectBrowserBody
 * @extends Component
 */
class ObjectBrowserBody extends Component {
  isMounted = false;
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    block: PropTypes.string.isRequired,
    mode: PropTypes.string.isRequired,
    data: PropTypes.any.isRequired,
    searchSubrequests: PropTypes.objectOf(PropTypes.any).isRequired,
    searchContent: PropTypes.func.isRequired,
    closeObjectBrowser: PropTypes.func.isRequired,
    onChangeBlock: PropTypes.func.isRequired,
    onSelectItem: PropTypes.func,
    dataName: PropTypes.string,
    maximumSelectionSize: PropTypes.number,
    contextURL: PropTypes.string,
    searchableTypes: PropTypes.arrayOf(PropTypes.string),
    sidebarContainer: PropTypes.object,
    initialPathFn: PropTypes.string,
    viewMode: PropTypes.string, // "list" or "grid"
    startInSearch: PropTypes.bool,
    defaultSearchMode: PropTypes.string, // "local" or "global"
    filterOnLanguage: PropTypes.bool,
    showNotPossibleTypes: PropTypes.bool,
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    image: '',
    href: '',
    onSelectItem: null,
    dataName: null,
    selectableTypes: [],
    searchableTypes: null,
    maximumSelectionSize: null,
    initialPathFn: null,
    viewMode: null,
    startInSearch: false,
    defaultSearchMode: 'global',
    filterOnLanguage: false,
    showNotPossibleTypes: true,
  };

  /**
   * Constructor
   * @method constructor
   * @param {Object} props Component properties
   * @constructs WysiwygEditor
   */
  constructor(props) {
    super(props);
    const initialPathFnFromRegistry =
      config.settings.objectBrowser.initialPathFn[this.props.initialPathFn];
    const initialPathFn = initialPathFnFromRegistry
      ? initialPathFnFromRegistry
      : // The default initialPathFn returns the current path (contextURL).
        (path, _lang) => path;
    const selectedImage =
      this.props.mode === 'multiple'
        ? ''
        : this.props.mode === 'image' && this.props.data?.url
          ? flattenToAppURL(this.props.data.url)
          : '';
    const selectedHref =
      this.props.mode === 'multiple'
        ? ''
        : this.props.mode === 'link' && this.props.data?.href
          ? flattenToAppURL(this.props.data.href)
          : '';
    this.state = {
      currentImageFolder:
        this.props.mode === 'multiple'
          ? '/'
          : this.props.mode === 'image' && this.props.data?.url
            ? getParentURL(this.props.data.url)
            : '/',
      currentLinkFolder:
        this.props.mode === 'multiple'
          ? '/'
          : this.props.mode === 'link' && this.props.data?.href
            ? getParentURL(this.props.data.href)
            : '/',
      ...this.navigateState(
        initialPathFn(this.props.contextURL, this.props.lang),
      ),
      selectedImage,
      selectedHref,
      showSearchInput: this.props.startInSearch,
      view:
        this.props.viewMode || (this.props.mode === 'image' ? 'icons' : 'list'),
      showFull: true,
      lastSearchIsPending: false,
      searchLength: INITIAL_SEARCH_LENGTH,
      localSearchOnly: this.props.defaultSearchMode === 'local',
      searchText: '',
      sortBy: 'default',
      selectedItems:
        this.props.mode === 'multiple'
          ? this.props.data
          : ((selected) => (selected ? [selected] : []))(
              this.props.mode === 'image' ? selectedImage : selectedHref,
            ),
      uploadId: uuid(),
      forceReloadCnt: 0,
      uploadStatus: {},
    };
    this.searchInputRef = React.createRef();
  }

  /**
   * Component did mount
   * @method componentDidMount
   * @returns {undefined}
   */
  componentDidMount() {
    this.isMounted = true;
    this.doSearch();

    // When starting in search mode, focus the input field
    if (this.props.startInSearch && this.searchInputRef?.current) {
      this.searchInputRef.current.focus();
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  navigateState = (id) =>
    id
      ? {
          parentFolder: join(id.split('/').slice(0, -1), '/') || '/',
          currentFolder: id,
        }
      : {
          parentFolder: '/',
          currentFolder: this.getLanguageRoot(),
        };

  navigateTo = (id) => {
    this.setState(() => ({
      ...this.navigateState(id),
      searchLength: INITIAL_SEARCH_LENGTH,
    }));
  };

  toggleSearchInput = () =>
    this.setState(
      (state) => ({
        showSearchInput: !state.showSearchInput,
        // reset the search text when closing the search input
        ...(!state.showSearchInput ? {} : { searchText: '' }),
        searchLength: INITIAL_SEARCH_LENGTH,
      }),
      () => {
        if (this.searchInputRef?.current) {
          this.searchInputRef.current.focus();
        }
      },
    );

  toggleView = () =>
    this.setState((prevState) => ({
      view: prevState.view === 'icons' ? 'list' : 'icons',
    }));

  toggleSortBy = () =>
    this.setState((state) => ({
      sortBy: state.sortBy === 'default' ? 'alphabetical' : 'default',
    }));

  onSearchTextChanged = (e) => {
    const searchText = e.target.value;
    this.setState({
      searchText,
      searchLength: INITIAL_SEARCH_LENGTH,
    });
    // If a directory was entered, navigate to it
    if (searchText.startsWith('/')) {
      this.navigateTo(searchText);
    }
  };

  getLanguageRoot = () => {
    if (!this.props.filterOnLanguage) {
      return '/';
    } else {
      if (this.props.lang === 'en') {
        return '/en';
      } else if (this.props.lang === 'fr') {
        return '/fr';
      } else {
        return '/de';
      }
    }
  };

  doSearch = () => {
    const state = this.state;
    // In image mode, the searchable types default to the image types which
    // can be overridden with the property if specified.
    // All this can be overridden with the showNotPossibleTypes property, which
    // if specified, portal_type is not filtered at all.
    const portal_type = this.props.showNotPossibleTypes
      ? undefined
      : this.props.mode === 'image'
        ? this.props.searchableTypes || config.settings.imageObjects
        : this.props.searchableTypes;
    // If a path was entered, we consider the text as empty.
    // At this point, the navigation has alredy been done.
    const text = this.getSearchText();
    if (this.goingToSearch(text)) {
      // Text search
      this.props.searchContent(
        state.localSearchOnly
          ? this.state.currentFolder
          : this.getLanguageRoot(),
        {
          SearchableText: `${text}*`,
          metadata_fields: '_all',
          portal_type,
          b_size: state.searchLength,
        },
        `${this.props.block}-${this.props.mode}`,
      );
    } else {
      // Empty or too short text, just list the current folder
      const mode = this.props.mode;
      const sort_on =
        this.state.sortBy === 'default'
          ? 'getObjPositionInParent'
          : 'sortable_title';
      const currentSelected =
        mode === 'multiple'
          ? ''
          : mode === 'image'
            ? state.selectedImage
            : state.selectedHref;
      if (currentSelected && isInternalURL(currentSelected)) {
        this.props.searchContent(
          getParentURL(currentSelected),
          {
            'path.depth': 1,
            sort_on,
            metadata_fields: '_all',
            portal_type,
            b_size: state.searchLength,
          },
          `${this.props.block}-${mode}`,
        );
      } else {
        this.props.searchContent(
          this.state.currentFolder,
          {
            'path.depth': 1,
            sort_on,
            metadata_fields: '_all',
            portal_type,
            b_size: state.searchLength,
          },
          `${this.props.block}-${mode}`,
          { b_size: INITIAL_SEARCH_LENGTH },
        );
      }
    }
  };

  /** Propagate a single selection change to parent
   * @method onSelectItemInParent
   * @param {Object} item The selected item
   */
  onSelectItemInParent = (item) => {
    const url = item['@id'];
    const { block, data, mode, dataName, onChangeBlock } = this.props;

    const updateState = (mode) => {
      switch (mode) {
        case 'image':
          this.setState({
            selectedImage: url,
            currentImageFolder: getParentURL(url),
          });
          break;
        case 'link':
          this.setState({
            selectedHref: url,
            currentLinkFolder: getParentURL(url),
          });
          break;
        default:
          break;
      }
    };

    if (dataName) {
      onChangeBlock(block, {
        ...data,
        [dataName]: url,
      });
    } else if (this.props.onSelectItem) {
      this.props.onSelectItem(url, item);
    } else if (mode === 'image') {
      onChangeBlock(block, {
        ...data,
        url: flattenToAppURL(item.getURL),
        alt: '',
      });
    } else if (mode === 'link') {
      onChangeBlock(block, {
        ...data,
        href: flattenToAppURL(url),
      });
    }
    updateState(mode);
  };

  // onChangeBlockData = (key, value) => {
  //   this.props.onChangeBlock(this.props.block, {
  //     ...this.props.data,
  //     [key]: value,
  //   });
  // };

  /**
   * Propagate the selection changes to parent.
   * @method onPropagateSelectionToParent
   */
  onPropagateSelectionToParent = (selectedItems = undefined) => {
    const { mode } = this.props;
    selectedItems = [...(selectedItems || this.state.selectedItems)];
    // Deselect any item that is not selected in the new selection
    let data = this.props.data;
    data = Array.isArray(data) ? data : data ? [data] : [];
    for (const item of data) {
      const url = item['@id'];
      if (selectedItems.find((i) => i['@id'] === url)) {
        selectedItems = selectedItems.filter((i) => i['@id'] !== url);
      } else if (mode === 'multiple') {
        // remove the existing selection (multiple mode only)
        this.onSelectItemInParent(item);
      }
    }
    // Select any item that is selected in the new selection
    for (const item of selectedItems) {
      this.onSelectItemInParent(item);
    }
  };

  onConfirm = () => {
    this.onPropagateSelectionToParent();
    this.props.closeObjectBrowser();
  };

  /**
   * Change the selection in the local state only.
   * The actual selection is only communicated to the parent
   * when the user clicks the confirm button.
   * @method onChangeSelection
   * @param {string} item The item to select/deselect
   */
  onChangeSelection = (item, { propagate } = {}) => {
    const url = item['@id'];
    const { mode } = this.props;
    let selectedItems = [...this.state.selectedItems];
    if (mode === 'multiple' && selectedItems.find((d) => d['@id'] === url)) {
      // remove the existing selection
      selectedItems = selectedItems.filter((d) => d['@id'] !== url);
    } else {
      // add the new selection
      if (
        mode === 'multiple' &&
        this.props.maximumSelectionSize &&
        selectedItems.length >= this.props.maximumSelectionSize
      ) {
        // do nothing, we reached the maximum selection size
        return;
      }
      if (mode !== 'multiple') {
        // single mode, remove any previous selection before adding the new one
        selectedItems = [];
      }
      selectedItems.push(item);
    }
    this.setState({
      selectedItems,
    });
    if (propagate) {
      this.onPropagateSelectionToParent(selectedItems);
    }
  };

  isSelectable = (item) => {
    const { maximumSelectionSize, data, mode, selectableTypes } = this.props;
    if (
      maximumSelectionSize &&
      data &&
      mode === 'multiple' &&
      maximumSelectionSize <= data.length
    )
      // The item should actually be selectable, but only for removing it from already selected items list.
      // handleClickOnItem will handle the deselection logic.
      // The item is not selectable if we reached/exceeded maximumSelectionSize and is not already selected.
      return data.some(
        (d) => flattenToAppURL(d['@id']) === flattenToAppURL(item['@id']),
      );
    return selectableTypes.length > 0
      ? selectableTypes.indexOf(item['@type']) >= 0
      : true;
  };

  handleClickOnItem = (item) => {
    if (this.props.mode === 'image') {
      if (item.is_folderish) {
        this.navigateTo(item['@id']);
      }
      if (config.settings.imageObjects.includes(item['@type'])) {
        this.onChangeSelection(item);
      }
    } else {
      if (this.isSelectable(item)) {
        this.onChangeSelection(item);
        // }
      } else {
        this.navigateTo(item['@id']);
      }
    }
  };

  handleDoubleClickOnItem = (item) => {
    if (this.props.mode === 'image') {
      if (item.is_folderish) {
        this.navigateTo(item['@id']);
      }
      if (config.settings.imageObjects.includes(item['@type'])) {
        // immediately select the item and close the browser
        this.onChangeSelection(item, { propagate: true });
        this.props.closeObjectBrowser();
      }
    } else {
      if (this.isSelectable(item)) {
        // immediately select the item and close the browser
        this.onChangeSelection(item, { propagate: true });
        this.props.closeObjectBrowser();
      } else {
        this.navigateTo(item['@id']);
      }
    }
  };

  toggleFullSize = () => {
    const aside = this.props.sidebarContainer;

    if (!aside) {
      // eslint-disable-next-line no-console
      console.warn('No sidebar container found to toggle full size.');
      return;
    }

    if (this.state.showFull) {
      aside.classList.add('full-size');
    } else {
      aside.classList.remove('full-size');
    }

    this.setState((prev) => ({ showFull: !prev.showFull }));
  };

  currentSearchResults = (props = undefined) => {
    props = props || this.props;
    return props.searchSubrequests[`${props.block}-${props.mode}`];
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.currentSearchResults(prevProps)?.loaded &&
      this.currentSearchResults()?.loaded &&
      this.state.lastSearchIsPending
    ) {
      // We have a pending search request, execute it and clear the lastSearch
      //      // while keeping the last search cached
      // const [text, fOptions, subrequest] = this.state.lastSearch;
      this.setState({
        lastSearchIsPending: false,
      });
      this.doSearch();
    }
    if (
      this.state.searchLength > prevState.searchLength ||
      this.state.localSearchOnly !== prevState.localSearchOnly ||
      this.state.currentFolder !== prevState.currentFolder ||
      this.state.searchableTypes !== prevState.searchableTypes ||
      this.state.searchText !== prevState.searchText ||
      this.state.parentFolder !== prevState.parentFolder ||
      this.getSortingOrder(this.state) !== this.getSortingOrder(prevState) ||
      this.state.forceReloadCnt !== prevState.forceReloadCnt ||
      // the rest is not strictly needed, but added for prudence
      this.props.contextURL !== prevProps.contextURL ||
      this.props.filterOnLanguage !== prevProps.filterOnLanguage ||
      this.props.lang !== prevProps.lang ||
      this.props.showNotPossibleTypes !== prevProps.showNotPossibleTypes
    ) {
      this.searchContentDebounced();
    }
  }

  searchContentDebounced = () => {
    if (!this.currentSearchResults()?.loading) {
      // Execute the search, when there is no pending request
      this.doSearch();
    } else {
      // Mark that we need a new search when the pending one is finished
      this.setState({
        lastSearchIsPending: true,
      });
    }
  };

  onLoadMore = () => {
    this.setState((prevState) => ({
      searchLength: prevState.searchLength + INCREASE_SEARCH_LENGTH,
    }));
  };

  onToggleLocalSearchOnly = (isSelected) => {
    this.setState((state) => ({
      localSearchOnly: !isSelected,
      searchLength: INITIAL_SEARCH_LENGTH,
    }));
  };

  getSearchText = () => {
    // If a path was entered, we consider the text as empty.
    const searchText = this.state.searchText;
    return flattenToAppURL(searchText).startsWith('/') ? '' : searchText;
  };

  // We only search if the text is longer than 2 characters
  goingToSearch = (searchText) => searchText.length > 2;

  getSearchMode = () => this.goingToSearch(this.getSearchText());

  getSortingOrder = (state) => {
    state = state || this.state;
    // If we are not actually searching by text, we display
    // the default order, since the order is currently irrelevant in this case.
    return this.getSearchMode() ? 'default' : state.sortBy;
  };

  getSelected = () =>
    this.props.mode === 'multiple'
      ? this.props.data
      : ((selected) => (selected ? [selected] : []))(
          this.props.mode === 'image'
            ? this.state.selectedImage
            : this.state.selectedHref,
        );

  uploadFileData = async (file) => {
    // Read file as Data URL so we get mime-type, encoding and data
    const reader = new FileReader();
    reader.readAsDataURL(file);
    const dataUrl = await new Promise((resolve) => {
      reader.onload = (evt) => resolve(evt.target.result);
    });
    // const dataUrl = await readAsDataURL(file);
    // dataUrl is "data:<mime>;<encoding>,<base64data>"
    const fields = dataUrl.match(/^data:(.*?);(.*?),(.*)$/) || [];
    const contentType = fields[1] || '';
    const encoding = fields[2] || '';
    const data = fields[3] || '';
    if (!this.isMounted) {
      return;
    }
    const isImage = contentType?.startsWith('image/');
    const isValid =
      !config.settings.maxFileUploadSize ||
      file.size <= config.settings.maxFileUploadSize;
    if (!isValid) {
      this.setState({
        fileUploadProgress: false,
        uploadStatus: {
          hasStatus: true,
          error: true,
          message: this.props.intl.formatMessage(messages.fileTooLarge, {
            limit: `${Math.floor(
              config.settings.maxFileUploadSize / 1024 / 1024,
            )}MB`,
          }),
        },
      });
      return;
    }
    return await new Promise((resolve, reject) => {
      this.setState({
        fileUploadProgress: true,
        uploadStatus: { hasStatus: false },
      });
      this.props
        .createContent(
          getBaseUrl(this.state.currentFolder),
          {
            '@type': isImage ? 'Image' : 'File',
            filename: file.name,
            title: file.name,
            [isImage ? 'image' : 'file']: {
              data,
              encoding,
              'content-type': contentType,
              filename: file.name,
            },
          },
          this.state.uploadId,
        )
        .then((data) => {
          if (this.isMounted) {
            this.setState({
              fileUploadProgress: false,
              uploadStatus: {
                hasStatus: true,
                success: true,
                message: this.props.intl.formatMessage(
                  messages.FileUploadSuccess,
                  {
                    filename: file.name,
                  },
                ),
              },
            });
          }
          resolve(data);
        })
        .catch((error) => {
          if (this.isMounted) {
            this.setState({
              fileUploadProgress: false,
              uploadStatus: {
                hasStatus: true,
                error: true,
                message: this.props.intl.formatMessage(
                  messages.FileUploadError,
                  {
                    error: error.message,
                  },
                ),
              },
            });
          }
          reject(error);
        });
    });
  };

  getUploadProgress = () => this.props.contentSubrequests[this.state.uploadId];

  uploadFile = () => {
    var input = document.createElement('input');
    input.type = 'file';

    input.onchange = async (e) => {
      var file = e.target.files[0];
      try {
        await this.uploadFileData(file);
      } catch (error) {
        return;
      }
      if (this.isMounted) {
        this.setState((prevState) => ({
          forceReloadCnt: prevState.forceReloadCnt + 1,
        }));
      }
    };

    input.click();
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const ViewSelectorButton = () => (
      <button
        onClick={this.toggleView}
        aria-label={this.props.intl.formatMessage(
          this.state.view === 'list' ? messages.iconView : messages.listView,
        )}
      >
        <Icon
          name={this.state.view === 'list' ? iconsSVG : listSVG}
          size="24px"
          title={this.props.intl.formatMessage(
            this.state.view === 'list' ? messages.iconView : messages.listView,
          )}
        />
      </button>
    );
    const selectedCounter = Math.min(this.state.selectedItems.length, 99);

    const isSearchMode = this.getSearchMode();

    return (
      <Segment.Group raised className="object-browser">
        <header className="header pulled">
          <div className="vertical divider" />
          {this.state.currentFolder === '/' ||
          this.state.currentFolder === this.getLanguageRoot() ? (
            <>
              {this.props.mode === 'image' ? (
                <Icon name={folderSVG} size="24px" />
              ) : (
                <Icon name={linkSVG} size="24px" />
              )}
            </>
          ) : (
            <button
              aria-label={this.props.intl.formatMessage(messages.back)}
              onClick={() => this.toggleFullSize()}
            >
              <Icon
                className="full-size-icon"
                name={this.state.showFull ? expandSVG : collapseSVG}
              />
            </button>
          )}
          {this.props.mode === 'image' ? (
            <h2>
              <FormattedMessage
                id="Choose Image"
                defaultMessage="Choose Image"
              />
            </h2>
          ) : (
            <h2>
              <FormattedMessage
                id="Choose Target"
                defaultMessage="Choose Target"
              />
            </h2>
          )}
          <button className="clearSVG" onClick={this.props.closeObjectBrowser}>
            <Icon name={clearSVG} size="30px" />
          </button>
          <button
            className={`confirmButton ${selectedCounter > 0 ? 'confirmButtonEnabled' : 'confirmButtonDisabled'}`}
            onClick={this.onConfirm}
          >
            {selectedCounter > 0 ? (
              <span
                className={`selectCounter ${selectedCounter > 9 ? 'confirmButtonDoubleDigit' : ''}`}
              >
                {selectedCounter}
              </span>
            ) : null}
            <Icon
              name={checkSVG}
              size="30px"
              color={
                selectedCounter > 0
                  ? 'currentColor'
                  : 'var(--color-confirm-icon-disabled)'
              }
            />
          </button>
        </header>
        <Segment secondary className="breadcrumbs" vertical>
          <Breadcrumb>
            {this.state.currentFolder !== '/' ? (
              this.state.currentFolder.split('/').map((item, index, items) => {
                return (
                  <React.Fragment key={`divider-${item}-${index}`}>
                    {index === 0 ? (
                      <Breadcrumb.Section
                        onClick={() => this.navigateTo('/')}
                        role="button"
                        aria-label={this.props.intl.formatMessage(
                          messages.home,
                        )}
                      >
                        <Icon
                          className="home-icon"
                          name={homeSVG}
                          size="18px"
                          title={this.props.intl.formatMessage(messages.home)}
                        />
                      </Breadcrumb.Section>
                    ) : (
                      <>
                        <Breadcrumb.Divider key={`divider-${item.url}`} />
                        <Breadcrumb.Section
                          role="button"
                          onClick={() =>
                            this.navigateTo(items.slice(0, index + 1).join('/'))
                          }
                        >
                          {item}
                        </Breadcrumb.Section>
                      </>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <Breadcrumb.Section
                onClick={() => this.navigateTo('/')}
                aria-label={this.props.intl.formatMessage(messages.home)}
              >
                <Icon
                  className="home-icon"
                  name={homeSVG}
                  role="button"
                  size="18px"
                  title={this.props.intl.formatMessage(messages.home)}
                />
              </Breadcrumb.Section>
            )}
          </Breadcrumb>
        </Segment>
        {!this.state.showSearchInput ? (
          <Segment secondary className="toolbar" vertical>
            <button
              className={
                this.state.fileUploadProgress
                  ? 'object-browser-button-inactive'
                  : 'object-browser-button-active'
              }
              aria-label={this.props.intl.formatMessage(messages.UploadFile)}
              disabled={this.state.fileUploadProgress}
              onClick={this.uploadFile}
            >
              <Icon
                name={cloudSVG}
                size="24px"
                title={this.props.intl.formatMessage(messages.UploadFile)}
              />
            </button>
            <ViewSelectorButton />
            <button
              className={
                this.state.showSearchInput
                  ? 'object-browser-button-inactive'
                  : 'object-browser-button-active'
              }
              disabled={isSearchMode}
              onClick={this.toggleSortBy}
              aria-label={this.props.intl.formatMessage(
                this.getSortingOrder() === 'alphabetical'
                  ? messages.SortByAlphabetical
                  : messages.SortByDefault,
              )}
            >
              {this.getSortingOrder() !== 'alphabetical' ? (
                <span
                  className="sort-by-alphabetical"
                  title={this.props.intl.formatMessage(
                    messages.SortByAlphabetical,
                  )}
                >
                  AZ
                </span>
              ) : (
                <Icon
                  name={sortDownSVG}
                  size="24px"
                  title={this.props.intl.formatMessage(messages.SortByDefault)}
                />
              )}
            </button>
            <button
              aria-label={this.props.intl.formatMessage(messages.search)}
              className="object-browser-button-toggle-search"
              onClick={this.toggleSearchInput}
            >
              <Icon
                name={searchSVG}
                size="24px"
                title={this.props.intl.formatMessage(messages.search)}
              />
            </button>
          </Segment>
        ) : (
          <Segment secondary className="searchbar" vertical>
            <Switch
              name="localSearchOnly"
              isSelected={!this.state.localSearchOnly}
              onChange={this.onToggleLocalSearchOnly}
            >
              {this.state.localSearchOnly
                ? this.props.intl.formatMessage(messages.Local)
                : this.props.filterLanguage
                  ? this.props.intl.formatMessage(messages.Language)
                  : this.props.intl.formatMessage(messages.Global)}
            </Switch>
            <Input
              className="search"
              ref={this.searchInputRef}
              onChange={this.onSearchTextChanged}
              placeholder={this.props.intl.formatMessage(
                messages.SearchInputPlaceholder,
              )}
            />
            <ViewSelectorButton />
            <button
              aria-label={this.props.intl.formatMessage(messages.CancelSearch)}
              onClick={this.toggleSearchInput}
            >
              <Icon name={clearSVG} size="20px" />
            </button>
          </Segment>
        )}
        {this.state.uploadStatus?.hasStatus && (
          <Segment
            vertical
            className={`file-upload-status ${
              this.state.uploadStatus.success ? 'success' : ''
            } ${this.state.uploadStatus.error ? 'error' : ''}`}
          >
            <div className="file-upload-status-message">
              {this.state.uploadStatus.message}
            </div>
            <div
              className="file-upload-close-icon"
              onClick={() => this.setState({ uploadStatus: {} })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  this.setState({ uploadStatus: {} });
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Close message"
            >
              <Icon name={clearSVG} size="20px" />
            </div>
          </Segment>
        )}

        <ObjectBrowserNav
          currentSearchResults={this.currentSearchResults()}
          selected={this.state.selectedItems}
          handleClickOnItem={this.handleClickOnItem}
          handleDoubleClickOnItem={this.handleDoubleClickOnItem}
          mode={this.props.mode}
          view={this.state.view}
          navigateTo={this.navigateTo}
          isSelectable={this.isSelectable}
          onLoadMore={this.onLoadMore}
          showMoreButton={
            this.currentSearchResults()?.items?.length <
            this.currentSearchResults()?.total
          }
          isSearchMode={isSearchMode}
        />
      </Segment.Group>
    );
  }
}

export default compose(
  injectIntl,
  connect(
    (state) => ({
      searchSubrequests: state.search.subrequests,
      contentSubrequests: state.content.subrequests,
      lang: state.intl.locale,
    }),
    { searchContent, createContent },
  ),
)(ObjectBrowserBody);
